import { useEffect, useState, useRef } from "react";
import DashboardShell from "@/components/DashboardShell";
import { CANDIDATE_NAV } from "./nav";
import api from "@/lib/api";
import { useTaxonomy } from "@/context/TaxonomyContext";
import MultiSelect from "@/components/MultiSelect";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Sparkles, Save, Download } from "lucide-react";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full text-sm border border-slate-300 rounded-md px-3 py-2 bg-white focus:border-[#D96B27] outline-none";

export default function CandidateProfile() {
  const tax = useTaxonomy();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const photoRef = useRef();

  const load = () => api.get("/candidates/me").then((r) => setP(r.data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/candidates/me", p);
      setP(r.data);
      toast.success("Profile saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const uploadResume = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/candidates/me/resume", fd);
      const parsed = r.data.parsed || {};
      const merged = { ...p, resume: r.data.resume };
      ["full_name", "mobile", "current_location", "current_company", "current_designation", "highest_education", "education_specialization"].forEach((k) => { if (parsed[k]) merged[k] = parsed[k]; });
      ["total_experience", "cable_experience"].forEach((k) => { if (parsed[k] != null) merged[k] = parsed[k]; });
      ["skills", "products", "processes", "industries", "departments", "certifications", "previous_companies"].forEach((k) => { if (Array.isArray(parsed[k]) && parsed[k].length) merged[k] = Array.from(new Set([...(merged[k] || []), ...parsed[k]])); });
      setP(merged);
      await api.put("/candidates/me", merged);
      toast.success(Object.keys(parsed).length ? "Resume uploaded & profile auto-filled with AI ✨" : "Resume uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/candidates/me/photo", fd);
      set("profile_photo_url", r.data.profile_photo_url);
      toast.success("Photo updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const deleteResume = async () => { await api.delete("/candidates/me/resume"); set("resume", null); toast.success("Resume removed"); };

  if (!p) return <DashboardShell nav={CANDIDATE_NAV}><div className="h-96 bg-white rounded-xl animate-pulse" /></DashboardShell>;

  const photoSrc = p.profile_photo_url && p.profile_photo_url.startsWith("/api") ? `${api.defaults.baseURL}${p.profile_photo_url.slice(4)}` : p.profile_photo_url;

  return (
    <DashboardShell nav={CANDIDATE_NAV}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">My Profile</h1>
          <p className="text-slate-500 text-sm">Completeness: <span className="font-mono-tech font-bold text-[#D96B27]">{p.completeness}%</span></p>
        </div>
        <button data-testid="save-profile-btn" onClick={save} disabled={saving} className="flex items-center gap-2 copper-gradient text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>

      {/* Resume + Photo */}
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#D96B27]" />Resume {p.resume ? "" : "(AI auto-fill)"}</h3>
          {p.resume ? (
            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-5 h-5 text-[#D96B27] shrink-0" />
                <div className="min-w-0"><div className="text-sm font-medium truncate">{p.resume.filename}</div><div className="text-xs text-slate-400">Uploaded {p.resume.uploaded_at?.slice(0, 10)}</div></div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a href={`${api.defaults.baseURL}/files/${p.resume.storage_path}?download=true`} target="_blank" rel="noreferrer" className="p-2 text-slate-500 hover:text-[#D96B27]"><Download className="w-4 h-4" /></a>
                <button onClick={deleteResume} data-testid="delete-resume-btn" className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ) : (
            <button onClick={() => fileRef.current.click()} data-testid="upload-resume-btn" disabled={uploading} className="w-full border-2 border-dashed border-slate-300 rounded-lg py-6 flex flex-col items-center hover:border-[#D96B27] transition-colors">
              <Upload className="w-6 h-6 text-slate-400 mb-2" />
              <span className="text-sm text-slate-600">{uploading ? "Uploading & parsing…" : "Upload PDF / DOC / DOCX"}</span>
              <span className="text-xs text-slate-400 mt-1">AI will auto-fill your profile</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" hidden onChange={uploadResume} />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-3">Profile Photo</h3>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center">
              {photoSrc ? <img src={photoSrc} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl font-display text-slate-400">{p.full_name?.[0] || "?"}</span>}
            </div>
            <button onClick={() => photoRef.current.click()} data-testid="upload-photo-btn" className="text-sm border border-slate-300 rounded-md px-4 py-2 hover:border-[#D96B27]">Change Photo</button>
            <input ref={photoRef} type="file" accept="image/*" hidden onChange={uploadPhoto} />
          </div>
        </div>
      </div>

      {/* Basic */}
      <Card title="Basic Information">
        <Field label="Full Name"><input className={inputCls} data-testid="field-full_name" value={p.full_name || ""} onChange={(e) => set("full_name", e.target.value)} /></Field>
        <Field label="Mobile"><input className={inputCls} data-testid="field-mobile" value={p.mobile || ""} onChange={(e) => set("mobile", e.target.value)} /></Field>
        <Field label="Date of Birth"><input className={inputCls} type="date" value={p.dob || ""} onChange={(e) => set("dob", e.target.value)} /></Field>
        <Field label="Gender"><select className={inputCls} value={p.gender || ""} onChange={(e) => set("gender", e.target.value)}><option value="">Select</option><option>Male</option><option>Female</option><option>Other</option></select></Field>
        <Field label="Current Location"><input list="locs" className={inputCls} value={p.current_location || ""} onChange={(e) => set("current_location", e.target.value)} /><datalist id="locs">{(tax.locations || []).map((l) => <option key={l} value={l} />)}</datalist></Field>
        <MultiSelect label="Preferred Locations" options={tax.locations || []} value={p.preferred_locations || []} onChange={(v) => set("preferred_locations", v)} testid="ms-preferred_locations" />
      </Card>

      {/* Professional */}
      <Card title="Professional Information">
        <Field label="Total Experience (yrs)"><input type="number" step="0.5" className={inputCls} data-testid="field-total_experience" value={p.total_experience ?? ""} onChange={(e) => set("total_experience", parseFloat(e.target.value) || 0)} /></Field>
        <Field label="Cable Industry Experience (yrs)"><input type="number" step="0.5" className={inputCls} data-testid="field-cable_experience" value={p.cable_experience ?? ""} onChange={(e) => set("cable_experience", parseFloat(e.target.value) || 0)} /></Field>
        <Field label="Current Company"><input className={inputCls} value={p.current_company || ""} onChange={(e) => set("current_company", e.target.value)} /></Field>
        <Field label="Current Designation"><input list="desigs" className={inputCls} value={p.current_designation || ""} onChange={(e) => set("current_designation", e.target.value)} /><datalist id="desigs">{(tax.designations || []).map((d) => <option key={d} value={d} />)}</datalist></Field>
        <Field label="Current Salary (LPA)"><input type="number" step="0.5" className={inputCls} value={p.current_salary ?? ""} onChange={(e) => set("current_salary", parseFloat(e.target.value) || null)} /></Field>
        <Field label="Expected Salary (LPA)"><input type="number" step="0.5" className={inputCls} value={p.expected_salary ?? ""} onChange={(e) => set("expected_salary", parseFloat(e.target.value) || null)} /></Field>
        <Field label="Notice Period"><select className={inputCls} value={p.notice_period || ""} onChange={(e) => set("notice_period", e.target.value)}><option value="">Select</option>{(tax.notice_periods || []).map((n) => <option key={n}>{n}</option>)}</select></Field>
        <Field label="Employment Type"><select className={inputCls} value={p.employment_type || ""} onChange={(e) => set("employment_type", e.target.value)}><option value="">Select</option>{(tax.employment_types || []).map((n) => <option key={n}>{n}</option>)}</select></Field>
        <Field label="Willing to Relocate"><select className={inputCls} value={p.willing_to_relocate ? "yes" : "no"} onChange={(e) => set("willing_to_relocate", e.target.value === "yes")}><option value="no">No</option><option value="yes">Yes</option></select></Field>
      </Card>

      {/* Industry specialization */}
      <Card title="Industry & Product Specialization">
        <MultiSelect label="Industry Category" options={tax.industries || []} value={p.industries || []} onChange={(v) => set("industries", v)} testid="ms-industries" />
        <MultiSelect label="Cable Product Experience" options={tax.products || []} value={p.products || []} onChange={(v) => set("products", v)} testid="ms-products" />
        <MultiSelect label="Conductor Experience" options={tax.conductors || []} value={p.conductors || []} onChange={(v) => set("conductors", v)} testid="ms-conductors" />
        <MultiSelect label="Technical Process Experience" options={tax.all_processes || []} value={p.processes || []} onChange={(v) => set("processes", v)} testid="ms-processes" />
        <MultiSelect label="Department / Job Function" options={tax.departments || []} value={p.departments || []} onChange={(v) => set("departments", v)} testid="ms-departments" />
        <MultiSelect label="Technical Skills" options={tax.skills || []} value={p.skills || []} onChange={(v) => set("skills", v)} testid="ms-skills" />
      </Card>

      {/* Education */}
      <Card title="Education">
        <Field label="Highest Education"><select className={inputCls} value={p.highest_education || ""} onChange={(e) => set("highest_education", e.target.value)}><option value="">Select</option>{(tax.education_degrees || []).map((d) => <option key={d}>{d}</option>)}</select></Field>
        <Field label="Specialization"><select className={inputCls} value={p.education_specialization || ""} onChange={(e) => set("education_specialization", e.target.value)}><option value="">Select</option>{(tax.education_specializations || []).map((d) => <option key={d}>{d}</option>)}</select></Field>
        <MultiSelect label="Certifications" options={["Six Sigma Green Belt", "Six Sigma Black Belt", "ISO 9001 Lead Auditor", "HV Testing Certified", "PLC Programming", "Project Management (PMP)"]} value={p.certifications || []} onChange={(v) => set("certifications", v)} testid="ms-certifications" />
      </Card>

      <div className="flex justify-end mt-5">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 copper-gradient text-white font-semibold px-6 py-2.5 rounded-lg disabled:opacity-60"><Save className="w-4 h-4" />{saving ? "Saving…" : "Save Profile"}</button>
      </div>
    </DashboardShell>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 mb-5">
    <h3 className="font-display font-semibold text-slate-900 mb-4">{title}</h3>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </div>
);
